// components/AsyncSingleSelectSearchbar.jsx
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/24/solid";

export default function AsyncSingleSelectSearchbar({
    token,
    value = null,
    onChange,
    fetcher, // async ({ token, q, page, pageSize }) => response
    getOptionLabel,
    getOptionValue,
    pageSize = 25,
    placeholder = "Seleziona...",
    className = "",
    inputClassName = "",
    optionsClassName = "max-h-64",
}) {
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);

    const buttonRef = useRef(null);
    const inputRef = useRef(null);
    const reqIdRef = useRef(0);

    const loadOptions = async ({ reset = false, q = query, force = false } = {}) => {
        if (!fetcher) return;
        if (loading && !force) return;

        const currentPage = reset ? 1 : page;
        const reqId = ++reqIdRef.current;

        setLoading(true);

        try {
        const res = await fetcher({
            token,
            q: q?.trim() || null,
            page: currentPage,
            pageSize,
        });

        if (reqId !== reqIdRef.current) return;

        const results = Array.isArray(res?.results)
            ? res.results
            : Array.isArray(res)
            ? res
            : [];

        setOptions((prev) => {
            const next = reset ? results : [...prev, ...results];

            const map = new Map(
            next.map((item) => [String(getOptionValue(item)), item])
            );

            return Array.from(map.values());
        });

        setHasMore(Boolean(res?.next));
        setPage(currentPage + 1);
        } catch (error) {
        console.error("Errore caricamento opzioni:", error);
        setHasMore(false);
        } finally {
        if (reqId === reqIdRef.current) setLoading(false);
        }
    };

    const resetAndSearch = (nextQuery = "") => {
        setQuery(nextQuery);
        setOptions([]);
        setPage(1);
        setHasMore(true);

        loadOptions({
            reset: true,
            q: nextQuery,
            force: true,
        });
    };

    const handleFocus = () => {
        if (options.length === 0 && !loading) {
        loadOptions({ reset: true, q: query });
        }
    };

    const handleInputChange = (e) => {
        resetAndSearch(e.target.value);
    };

    const handleScroll = (e) => {
        const el = e.currentTarget;

        if (!hasMore || loading) return;

        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) {
        loadOptions();
        }
    };

    const clearValue = (e) => {
        e.stopPropagation();
        onChange?.(null);
        setQuery("");
        setOptions([]);
        setPage(1);
        setHasMore(true);
        inputRef.current?.focus();
    };

    return (
        <Combobox value={value} onChange={onChange} nullable>
            {({ open }) => (
                <div className={`relative w-full ${className}`}>
                    <div
                        className="relative rounded-md bg-white cursor-text"
                        onMouseDown={(e) => {
                            if (e.target.closest("[data-ignore-wrapper-click]")) {
                                return;
                            }

                            e.preventDefault();
                            inputRef.current?.focus();

                            if (!open) {
                                buttonRef.current?.click();
                            }

                            if (options.length === 0 && !loading) {
                                loadOptions({ reset: true, q: query });
                            }
                        }}
                    >
                        <Combobox.Input
                            ref={inputRef}
                            className={`
                                h-[42px] w-full bg-transparent border border-gray-300 rounded-md
                                shadow-md px-3 pr-16 text-sm text-gray-700
                                outline-none focus:outline-none
                                placeholder:text-gray-400
                                focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400
                                ${inputClassName}
                            `}
                            displayValue={(item) => (item ? getOptionLabel(item) : query)}
                            onChange={handleInputChange}
                            onFocus={handleFocus}
                            placeholder={placeholder}
                        />

                        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
                            {value && (
                                <button
                                type="button"
                                data-ignore-wrapper-click
                                onClick={clearValue}
                                className="rounded p-1 text-gray-400 hover:text-gray-600 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                                >
                                <XMarkIcon className="h-4 w-4" />
                                </button>
                            )}

                            <Combobox.Button
                                ref={buttonRef}
                                data-ignore-wrapper-click
                                className="rounded p-1 text-gray-400 hover:text-gray-600 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                                onClick={() => {
                                    if (!open && options.length === 0) {
                                        loadOptions({ reset: true, q: query });
                                    }
                                }}
                            >
                                <ChevronUpDownIcon className="h-5 w-5" />
                            </Combobox.Button>
                        </div>
                    </div>

                    <Transition
                        as={Fragment}
                        show={open}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Combobox.Options
                            onScroll={handleScroll}
                            className={`
                                absolute z-30 mt-1 w-full overflow-auto rounded-md
                                bg-white py-1 text-sm shadow-lg ring-1 ring-black/5
                                focus:outline-none
                                ${optionsClassName}
                            `}
                        >
                            {!loading && options.length === 0 && (
                                <div className="px-3 py-2 text-gray-500">
                                Nessun risultato
                                </div>
                            )}

                            {options.map((option) => (
                                <Combobox.Option
                                key={getOptionValue(option)}
                                value={option}
                                className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                                    active ? "bg-indigo-50 text-gray-700" : "text-gray-700"
                                    }`
                                }
                                >
                                {({ selected }) => (
                                    <>
                                    <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                        {getOptionLabel(option)}
                                    </span>

                                    {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-indigo-600">
                                        <CheckIcon className="h-5 w-5" />
                                        </span>
                                    )}
                                    </>
                                )}
                                </Combobox.Option>
                            ))}

                            {loading && (
                                <div className="px-3 py-2 text-gray-500">
                                Caricamento…
                                </div>
                            )}
                        </Combobox.Options>
                    </Transition>
                </div>
            )}
        </Combobox>
    );
}